import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Alert, 
  Snackbar, 
  Box, 
  CircularProgress, 
  Typography, 
  Paper,
  styled
} from '@mui/material';
import MessageComposer from './MessageComposer';
import NotificationService, { Personnel } from '../../services/NotificationService';
import { useAuth } from '../../contexts/AuthContext';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useTranslation } from 'react-i18next';

// Özelleştirilmiş bileşenler
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
}));

const BulkMessage: React.FC = () => {
  const { t } = useTranslation('common2');
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const { currentUser } = useAuth();
  
  useEffect(() => {
    const loadPersonnel = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!currentUser) {
          setError(t('messages.bulkMessage.errors.noSession'));
          setLoading(false);
          return;
        }
        
        // Kullanıcının şirket ID'sini al
        const userRef = await NotificationService.getCurrentUserCompany(currentUser.uid);
        if (!userRef) {
          setError(t('messages.bulkMessage.errors.noCompany'));
          setLoading(false);
          return;
        }
        
        // Tüm personeli getir
        const personnelList = await NotificationService.getAllPersonnel(userRef);
        setPersonnel(personnelList);
      } catch (error) {
        console.error('Personel yüklenirken hata:', error);
        setError(t('messages.bulkMessage.errors.loadingError'));
      } finally {
        setLoading(false);
      }
    };
    
    loadPersonnel();
  }, [currentUser, t]);
  
  const handleSendMessage = async (title: string, body: string) => {
    if (personnel.length === 0) {
      setError(t('messages.bulkMessage.errors.noPersonnel'));
      return;
    }
    
    try {
      setSending(true);
      setError(null);
      
      if (!currentUser) {
        setError(t('messages.bulkMessage.errors.noSession'));
        return;
      }
      
      // Kullanıcının şirket ID'sini al
      const companyId = await NotificationService.getCurrentUserCompany(currentUser.uid);
      if (!companyId) {
        setError(t('messages.bulkMessage.errors.noCompany'));
        return;
      }
      
      // Tüm personelin ID'lerini al
      const allPersonnelIds = personnel.map(person => person.id);
      
      // Bildirimi gönder
      const result = await NotificationService.sendNotification(
        companyId,
        allPersonnelIds,
        title,
        body
      );
      
      // NOT: sendNotification artık tek çağrıda hem kaydeder hem FCM gönderir;
      // eski Node /api/send-notification toplu çağrısı kaldırıldı.
      if (result.success) {
        setSuccessMessage(t('messages.bulkMessage.success', { count: result.sentCount }));
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Bildirim gönderilirken hata:', error);
      setError(t('messages.bulkMessage.errors.sendingError'));
    } finally {
      setSending(false);
    }
  };
  
  // Bildirim mesajlarını kapatma
  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };
  
  return (
    <Box>
      {/* Hata mesajı */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Personel Bilgisi */}
      {!loading && personnel.length > 0 && (
        <StyledPaper elevation={0}>
          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            bgcolor: 'primary.light',
            color: 'primary.contrastText'
          }}>
            <PeopleAltIcon fontSize="large" />
            <Box>
              <Typography variant="h6">
                {t('messages.bulkMessage.title')}
              </Typography>
              <Typography variant="body2">
                {t('messages.bulkMessage.description', { count: personnel.length })}
              </Typography>
            </Box>
          </Box>
        </StyledPaper>
      )}
      
      {/* Yükleniyor göstergesi */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {!loading && personnel.length === 0 && !error && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('messages.bulkMessage.noPersonnel')}
        </Alert>
      )}
      
      {!loading && personnel.length > 0 && (
        <Grid container justifyContent="center">
          {/* Mesaj Oluşturucu */}
          <Grid item xs={12} md={8}>
            <MessageComposer
              onSend={handleSendMessage}
              isLoading={sending}
              recipientCount={personnel.length}
            />
          </Grid>
        </Grid>
      )}
      
      {/* Başarı bildirimi */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BulkMessage; 